import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, Hash, UserPlus, Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import QRCodeCard from '@/components/QRCodeCard';

export const CollaboratorInfoCard = ({ collaborator, API, onUserCreated }) => {
  const [hasUserAccount, setHasUserAccount] = useState(false);
  const [linkedUser, setLinkedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('collaborator');

  useEffect(() => {
    checkUserAccount();
  }, [collaborator.id]);

  const checkUserAccount = async () => {
    try {
      const { data } = await axios.get(`${API}/users`);
      const linked = data.find(u => u.linked_collaborator_id === collaborator.id);
      if (linked) {
        setHasUserAccount(true);
        setLinkedUser(linked);
      } else {
        setHasUserAccount(false);
        setLinkedUser(null);
      }
    } catch (error) {
      // User might not have access to users list
      setHasUserAccount(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (!collaborator.email) {
      toast.error('Le collaborateur doit avoir un email pour créer un compte');
      return;
    }
    
    if (!password || password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/users`, {
        email: collaborator.email,
        password: password,
        name: collaborator.name,
        role: role,
        linked_collaborator_id: collaborator.id
      });
      toast.success('Compte utilisateur créé avec succès');
      setDialogOpen(false);
      setPassword('');
      setRole('collaborator');
      checkUserAccount();
      if (onUserCreated) onUserCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Informations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20">
                {collaborator.photo && <AvatarImage src={collaborator.photo} alt={collaborator.name} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {collaborator.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <h2 className="text-2xl font-heading font-bold">{collaborator.name}</h2>
                {collaborator.matricule && (
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-primary" />
                    <Badge variant="secondary" className="font-mono text-base px-3 py-1">
                      {collaborator.matricule}
                    </Badge>
                  </div>
                )}
                {collaborator.email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" /><span>{collaborator.email}</span>
                  </div>
                )}
                {collaborator.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" /><span>{collaborator.phone}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* User Account Section */}
            <div className="mt-6 pt-4 border-t">
              {hasUserAccount ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Compte utilisateur actif ({linkedUser?.role})
                  </span>
                </div>
              ) : (
                <Button 
                  onClick={() => setDialogOpen(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={!collaborator.email}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer un compte utilisateur
                </Button>
              )}
              {!collaborator.email && !hasUserAccount && (
                <p className="text-xs text-muted-foreground mt-2">
                  Ajoutez un email au collaborateur pour pouvoir créer un compte
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {collaborator.matricule && (
          <QRCodeCard 
            matricule={collaborator.matricule} 
            collaboratorName={collaborator.name} 
          />
        )}
      </div>

      {/* Create User Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Créer un compte utilisateur
            </DialogTitle>
            <DialogDescription>
              Créer un compte pour {collaborator.name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm">
                <span className="text-muted-foreground">Email de connexion :</span>
                <span className="ml-2 font-medium">{collaborator.email}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="pl-10 rounded-lg"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Rôle</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="collaborator">Collaborateur</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button type="submit" className="w-full rounded-full" disabled={loading}>
              {loading ? 'Création...' : 'Créer le compte'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
